import { useState } from 'react';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Convert to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6nb01o5dsf7sr1n6le0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#4a1a4a] to-[#8b4a5a]"></div>
        
        {/* Decorative Element */}
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full blur-3xl"></div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-50 px-16 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://public.readdy.ai/ai/img_res/438d850b-cfd7-4e53-8173-46c58915909a.png" 
                alt="SCARLET SRL Logo" 
                className="h-12 w-auto object-contain"
              />
              <h1 className="text-white text-2xl font-medium tracking-wide">SCARLET SRL</h1>
            </div>
            <div className="flex gap-12">
              <a href="#home" className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer">Home</a>
              <a href="#products" className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer">Products</a>
              <a href="#about" className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer">About</a>
              <a href="#contact" className="text-white/90 hover:text-white transition-colors text-sm font-medium cursor-pointer">Contact</a>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-16 max-w-7xl">
          <h2 className="text-white font-bold text-7xl leading-tight max-w-4xl">
            Modern iOS Applications for Enhanced Security
          </h2>
          <p className="text-white/85 text-xl mt-10 max-w-3xl leading-relaxed">
            Expert team developing VPN and Cleaner solutions that prioritize user experience and mobile device protection
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#F8F9FA] py-32 px-20">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-block bg-white/80 border border-gray-200 rounded-full px-5 py-2 mb-4">
              <span className="text-gray-600 text-sm font-medium">// OUR PRODUCTS</span>
            </div>
            <h2 className="text-[#1A1A1A] font-bold text-6xl mb-5">VPN & Cleaner Applications</h2>
            <p className="text-[#6B7280] text-lg">Designed for the modern iOS ecosystem</p>
          </div>

          {/* Product Cards */}
          <div className="grid grid-cols-2 gap-12">
            {/* VPN Card */}
            <div className="bg-white rounded-3xl p-16 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-[#1A1A1A] font-bold text-3xl mb-5">VPN Protection</h3>
              <p className="text-[#6B7280] text-base leading-relaxed">
                Secure your internet connection with military-grade encryption. Browse privately and access content from anywhere while maintaining complete anonymity on your iOS device.
              </p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl p-16 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mb-8">
                <i className="ri-sparkling-line text-white text-3xl"></i>
              </div>
              <h3 className="text-[#1A1A1A] font-bold text-3xl mb-5">Device Cleaner</h3>
              <p className="text-[#6B7280] text-base leading-relaxed">
                Optimize your iPhone performance by removing unnecessary files and managing storage efficiently. Keep your device running smoothly with intelligent cleaning algorithms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-gradient-to-b from-[#4C1D95] to-[#7C2D12] py-36 px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-12">
            {/* Left Content */}
            <div className="col-span-7">
              <div className="inline-block bg-purple-300/40 rounded-lg px-4 py-2 mb-6">
                <span className="text-white text-xs font-medium">// ABOUT US</span>
              </div>
              <h2 className="text-white font-bold text-5xl leading-snug mb-8 max-w-xl">
                Expert Team Building the Future of iOS Security
              </h2>
              <p className="text-[#F3F4F6]/90 text-lg leading-relaxed mb-6">
                SCARLET SRL brings together seasoned developers and security specialists dedicated to creating applications that matter. Our VPN and Cleaner products represent years of research and development, focusing on user privacy and device optimization.
              </p>
              <p className="text-[#F3F4F6]/90 text-lg leading-relaxed">
                We believe in transparent development practices and continuous improvement, ensuring our applications meet the highest standards of the iOS ecosystem.
              </p>
            </div>

            {/* Right Visual */}
            <div className="col-span-5">
              <div className="w-full h-80 bg-[#E5E7EB] rounded-3xl overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/82a6f7ff2412055786aca878caa75f68.jpg" 
                  alt="SCARLET SRL Workspace" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="bg-white py-28 px-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif font-extrabold text-6xl text-[#0A0A0A] leading-tight max-w-2xl mb-16">
            Cutting-Edge Technology<br />Meets Intuitive Design
          </h2>

          <div className="grid grid-cols-3 gap-10">
            {/* Security Card */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-[#DBEAFE] rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-lock-line text-blue-600 text-4xl"></i>
              </div>
              <h3 className="font-serif font-bold text-2xl text-[#1A1A1A] mb-4">Bank-Level Encryption</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                AES-256 encryption protocol ensures your data remains completely secure during transmission and storage.
              </p>
            </div>

            {/* Performance Card */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-[#FEF3C7] rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-flashlight-line text-yellow-600 text-4xl"></i>
              </div>
              <h3 className="font-serif font-bold text-2xl text-[#1A1A1A] mb-4">Lightning Fast Performance</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Optimized algorithms deliver instant results without compromising your device battery life or processing power.
              </p>
            </div>

            {/* UX Card */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="w-20 h-20 bg-[#FCE7F3] rounded-full flex items-center justify-center mb-8 mx-auto">
                <i className="ri-heart-line text-pink-600 text-4xl"></i>
              </div>
              <h3 className="font-serif font-bold text-2xl text-[#1A1A1A] mb-4">Intuitive User Interface</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                Carefully crafted interfaces that feel native to iOS, requiring zero learning curve for immediate productivity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[600px] overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=abstract%20flowing%20gradient%20mesh%20with%20smooth%20curves%20transitioning%20from%20deep%20blue%20through%20vibrant%20purple%20to%20soft%20pink%2C%20modern%20digital%20background%20with%20fluid%20organic%20shapes%2C%20contemporary%20tech%20aesthetic%2C%20high-end%20premium%20design%2C%20seamless%20color%20blending%2C%20sophisticated%20gradient%20artwork&width=1920&height=600&seq=cta-gradient-001&orientation=landscape" 
          alt="Premium iOS Security" 
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/15"></div>
        
        <div className="relative z-10 h-full flex flex-col justify-between px-20 py-32">
          <h2 className="text-white font-bold text-7xl lowercase leading-tight max-w-3xl" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            experience premium ios security
          </h2>
          
          <div className="flex justify-between items-end">
            <div>
              <div className="text-white text-3xl font-normal tracking-widest" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>SCARLET SRL</div>
              <div className="w-48 h-0.5 bg-white/60 mt-2"></div>
            </div>
            
            <div className="text-right max-w-xl">
              <p className="text-white font-light text-2xl leading-relaxed" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                Protecting your digital life<br />with innovative solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#0F172A] mx-6 mb-6 rounded-t-[32px] py-20 px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-16">
            {/* Brand Statement */}
            <div className="col-span-4">
              <h3 className="text-white font-bold text-3xl mb-6">SCARLET SRL</h3>
              <p className="text-[#94A3B8] text-base mb-8">Modern iOS Applications</p>
              <p className="text-[#64748B] text-sm leading-relaxed max-w-sm">
                Expert team developing VPN and Cleaner solutions for enhanced mobile security and performance.
              </p>
            </div>

            {/* Products */}
            <div className="col-span-2">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">PRODUCTS</h4>
              <ul className="space-y-3">
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">VPN Protection</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Device Cleaner</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Security Suite</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Performance Tools</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Privacy Features</li>
              </ul>
            </div>

            {/* Company */}
            <div className="col-span-2">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">COMPANY</h4>
              <ul className="space-y-3">
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">About Us</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Technology</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Privacy Policy</li>
                <li className="text-[#94A3B8] text-sm cursor-pointer hover:text-white transition-colors whitespace-nowrap">Terms of Service</li>
              </ul>
            </div>

            {/* Contact Details */}
            <div className="col-span-4">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">CONTACT</h4>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-white text-base"></i>
                    <span className="text-white font-semibold text-sm">Address:</span>
                  </div>
                  <div className="text-[#94A3B8] text-sm leading-relaxed pl-6">
                    VIA PROSPERO SANTACROCE 131/C<br />
                    ROMA<br />
                    00167<br />
                    Italy
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-mail-line text-white text-base"></i>
                    <span className="text-white font-semibold text-sm">Email:</span>
                  </div>
                  <a href="mailto:help@scarletsrl.com" className="text-[#94A3B8] text-sm hover:text-white transition-colors pl-6 cursor-pointer">
                    help@scarletsrl.com
                  </a>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-phone-line text-white text-base"></i>
                    <span className="text-white font-semibold text-sm">Number:</span>
                  </div>
                  <a href="tel:+18207320869" className="text-[#94A3B8] text-sm hover:text-white transition-colors pl-6 cursor-pointer">
                    +1 8207320869
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div className="mt-10">
                <h5 className="text-white font-semibold text-base mb-4">Send us a message</h5>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                      className="w-full bg-[#1E293B] text-white placeholder-[#64748B] px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      required
                      className="w-full bg-[#1E293B] text-white placeholder-[#64748B] px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full bg-[#1E293B] text-white placeholder-[#64748B] px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                  {formStatus === 'success' && (
                    <div className="text-green-400 text-sm mt-2">{formMessage}</div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-red-400 text-sm mt-2">{formMessage}</div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Watermark */}
          <div className="relative mt-20">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-white/[0.03] font-bold text-[180px] leading-none pointer-events-none">
              SCARLET
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-[#1E293B] mt-16 pt-8">
            <p className="text-[#64748B] text-xs text-center">
              © 2025 SCARLET SRL. All rights reserved.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}